/**
 * THE ONE MAPPING FROM AN ONBOARDING STATE TO WORDS AND A TONE.
 *
 * Pure — no React, no fetching — on the model of `provisioningStages.js` and
 * for the same reason: the words and the tone are the part worth testing, and
 * they were locked inside a component that imports `axios` and
 * `react-hot-toast` and therefore could not be mounted.
 *
 * ─── WHAT WAS WRONG WITH WHAT THIS REPLACED ──────────────────────────────
 *
 * `OnboardingPanel.jsx` carried three private tone maps whose values were raw
 * Tailwind swatches — `bg-emerald-50 text-emerald-700`, `bg-amber-50
 * text-amber-800`, `text-slate-400`, sixteen pairs in all. Three separate
 * consequences, none of which a reviewer sees:
 *
 * 1. A FOURTH VISUAL LANGUAGE. `StatusBadge` exists precisely so that one
 *    status renders one way everywhere, and its own header says so: "never
 *    invent a new visual language per-page". This panel sits on the same
 *    tenant page as the Demo Connections card, which routes every chip through
 *    that component. The same idea — "this failed" — was drawn two ways, six
 *    inches apart.
 *
 * 2. IT WAS LIGHT-ONLY, AND SILENTLY SO. A hardcoded swatch does not move with
 *    the theme. Measured against the dark card these were: "Partly seeded"
 *    2.57:1, "Failed" 2.90:1, "In place" 3.32:1, every row label and caption
 *    3.83:1 — eight SC 1.4.3 failures that no amount of looking at the light
 *    theme reveals, because in the light theme they are all fine.
 *
 *    Two were failing in the LIGHT theme as well and had been all along:
 *    `text-slate-400` at 2.56:1, which carries the word "None" where a tenant
 *    has no primary administrator and "Not requested" where no sample data was
 *    asked for, and the "Not measured" chip at 4.34:1 — three of the most
 *    load-bearing absences on the panel, drawn in the faintest ink on it.
 *
 * 3. AMBER ON A HEALTHY TENANT. `pending` — an invitation sent and not yet
 *    accepted — was drawn in the warning tone. That is the NORMAL state of
 *    every tenant between creation and the customer's first sign-in, so a
 *    correctly-onboarding tenant showed a caution chip for days. A tone that
 *    is amber when nothing is wrong teaches an operator to stop reading amber.
 *    It is now the `sky` in-progress tone, which is what waiting is.
 *
 * ─── WHERE THE STATES COME FROM ──────────────────────────────────────────
 *
 * Every state below is transcribed from serving code. Nothing is invented, and
 * a state this build has not been told about is rendered as itself rather than
 * mapped onto one that looks similar.
 *
 *   invitation  `backend/modules/admin/services/invitation_service.py`
 *               INVITATION_STATES = (never_sent, pending, expired, accepted,
 *               delivery_failed) — this repository's own backend.
 *
 *   readiness   `backend/modules/admin/services/provisioning_readiness.py`
 *               READINESS_STATES = (provisioning, ready, attention,
 *               incomplete, legacy, unknown) — this repository's own backend.
 *
 *   sample data `tenant_sample_data.status`, which is written by the PLATFORM
 *               and only read here: `backend/shared/services/sample_data.py`
 *               emits `applied`, `partial` and `failed`, and
 *               `backend/shared/services/sample_data_removal.py` emits
 *               `removed` and `partially_removed`, both in
 *               sk-amroar/cluvik-platform. `not_requested` is not a stored
 *               status at all — it is the admin route's own default when there
 *               is no batch document, which is why it means "nobody asked",
 *               never "it ran and produced nothing".
 *
 *               THIS ONE IS NOT PINNED BY A TEST IN THIS REPOSITORY, because
 *               the code that produces it is not in this repository. The
 *               unknown-state branch below is what stands in for that pin.
 */

/** @see invitation_service.INVITATION_STATES */
export const INVITATION_STATES = [
  'never_sent', 'pending', 'expired', 'accepted', 'delivery_failed',
];

/** @see provisioning_readiness.READINESS_STATES */
export const READINESS_STATES = [
  'provisioning', 'ready', 'attention', 'incomplete', 'legacy', 'unknown',
];

/** @see sample_data.py / sample_data_removal.py in cluvik-platform. */
export const SAMPLE_STATES = [
  'not_requested', 'applied', 'partial', 'failed', 'partially_removed', 'removed',
];

/**
 * State -> `{ label, badge, meaning }`.
 *
 * `badge` is a `StatusBadge` key, PREFIXED. The three vocabularies share words
 * — `pending` is an invitation state and `failed` is both a readiness-adjacent
 * word and a sample-data one — and an unprefixed key would silently hand one
 * concept another concept's tone. The `demo_*`, `desired_*`, `attempted_*` and
 * `actual_*` families in StatusBadge.jsx are prefixed for exactly this reason.
 *
 * `meaning` is one sentence naming what the state is a fact ABOUT. The panel
 * puts four states in a column four rows tall and they are facts about four
 * different subjects: a runtime, an email, a person's account and a batch of
 * records. A reader given only the four words merges them.
 *
 * WRITING RULE for `label`, inherited from `provisioningStages.js`: name the
 * state, do not narrate an action, and never claim more than was measured.
 */
export const INVITATION_TONE = {
  never_sent: {
    label: 'Never sent',
    badge: 'invite_never_sent',
    meaning: 'No invitation has been issued. The administrator cannot sign in yet.',
  },
  pending: {
    label: 'Sent — awaiting setup',
    badge: 'invite_pending',
    meaning: 'Issued and still valid. Waiting on the recipient, not on Cluvik.',
  },
  expired: {
    label: 'Expired',
    badge: 'invite_expired',
    meaning: 'Issued and never accepted; the link no longer works. Sending a new one is the fix.',
  },
  accepted: {
    label: 'Accepted',
    badge: 'invite_accepted',
    meaning: 'The administrator completed setup and holds a password of their own.',
  },
  delivery_failed: {
    label: 'Delivery failed',
    badge: 'invite_delivery_failed',
    meaning: 'The credential exists; the provider refused the message. This is recoverable without re-issuing.',
  },
};

export const READINESS_TONE = {
  ready: {
    label: 'Ready',
    badge: 'readiness_ready',
    meaning: 'Every required provisioning step was verified against the runtime.',
  },
  provisioning: {
    label: 'Provisioning',
    badge: 'readiness_provisioning',
    meaning: 'The build is still running. Nothing here is a verdict yet.',
  },
  incomplete: {
    label: 'Incomplete',
    badge: 'readiness_incomplete',
    meaning: 'The run finished and something required was not established.',
  },
  /**
   * ASSESSED, INCOMPLETE, AND NOT REPAIRABLE FROM HERE.
   *
   * A tenant created by the vendored path has no provisioning job row and no
   * frozen specification. Its baseline really is missing things, so this is not
   * a softer `incomplete` — it is a DIFFERENT fact. `incomplete` says "the run
   * finished and something was not established", and for this tenant there was
   * no run; it also implies a remedy that does not exist, because Repair
   * refuses without an approved configuration to reconcile against.
   *
   * Deliberately NOT called "not assessed". It was assessed: specific absences
   * were measured. Slate rather than amber because there is nothing to press.
   */
  legacy: {
    label: 'Legacy tenant',
    badge: 'readiness_legacy',
    meaning: 'Created before the frozen-specification contract. Baseline items are '
      + 'missing and cannot be reconciled from here, because there is no approved '
      + 'configuration to repair against. This is not a failed provisioning run.',
  },
  attention: {
    label: 'Needs attention',
    badge: 'readiness_attention',
    meaning: 'The run reported a failure. The tenant is not usable as built.',
  },
  /**
   * NEVER SOFTENED INTO "READY", and never hardened into a failure either.
   * `unknown` is what the backend returns when a COMPLETED job carries no
   * verification report and no live check answered — there is no evidence, and
   * a state with no evidence is its own answer in both directions. Drawing it
   * green claims a measurement; drawing it red claims a failure.
   */
  unknown: {
    label: 'Not measured',
    badge: 'readiness_unknown',
    meaning: 'Nothing has verified this tenant. This is the absence of a measurement, not a passing one.',
  },
};

export const SAMPLE_TONE = {
  not_requested: {
    label: 'Not requested',
    badge: 'seed_not_requested',
    meaning: 'No seed was asked for. Different from a seed that ran and created nothing.',
  },
  applied: {
    label: 'In place',
    badge: 'seed_applied',
    meaning: 'Every record in the template was created.',
  },
  partial: {
    label: 'Partly seeded',
    badge: 'seed_partial',
    meaning: 'Some rows failed. The rest are in place and a repair re-runs only what is missing.',
  },
  failed: {
    label: 'Failed',
    badge: 'seed_failed',
    meaning: 'The seed did not complete. A repair is available.',
  },
  partially_removed: {
    label: 'Partly removed',
    badge: 'seed_partially_removed',
    meaning: 'Removal left records behind — typically ones the customer has since adopted.',
  },
  removed: {
    label: 'Removed',
    badge: 'seed_removed',
    meaning: 'The seeded records were deleted. Nothing from the template remains.',
  },
};

/**
 * A state this build does not know, made readable — and no more than that.
 *
 * `foo_bar` -> `Foo bar`. The same mechanical rule `provisioningStages.humanise`
 * uses, and deliberately no dictionary of near-misses: Admin and the Platform
 * deploy separately, so a newer Platform can name a sample-data status this
 * build has never received, and every rule that guesses at its meaning states
 * something nobody defined.
 */
const humanise = (state) => {
  const s = String(state).replace(/[_-]+/g, ' ').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
};

/**
 * The one description, for any of the three axes.
 *
 * Returns `{ state, label, badge, meaning, known }`, or null when there is no
 * state at all. An unrecognised state gets the MUTED tone — never a tone
 * borrowed from a state whose name it resembles — and `known: false` lets the
 * caller say so in words without changing them.
 */
const describe = (map, state, fallbackState) => {
  const key = String(state ?? '').trim() || String(fallbackState ?? '').trim();
  if (!key) return null;
  const hit = map[key];
  if (hit) return { state: key, ...hit, known: true };
  return {
    state: key,
    label: humanise(key),
    badge: 'unknown',
    meaning: 'This build has no description for this state. It is shown as the server named it, rather than mapped onto one that looks similar.',
    known: false,
  };
};

export const describeInvitation = (state) =>
  describe(INVITATION_TONE, state, 'never_sent');

export const describeReadiness = (state) =>
  describe(READINESS_TONE, state, 'unknown');

export const describeSampleData = (state) =>
  describe(SAMPLE_TONE, state, 'not_requested');

/**
 * Whether a repair is on the table for the sample-data batch.
 *
 * The two states the backend itself treats as retryable, named here rather
 * than inlined in JSX so the button and the caption cannot disagree about
 * which they are.
 */
export const SAMPLE_REPAIRABLE = ['partial', 'failed'];

export const isSampleRepairable = (state) =>
  SAMPLE_REPAIRABLE.includes(String(state || ''));

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/**
 * The one sentence about HOW MUCH sample data there is, or null when there
 * is nothing to count.
 *
 * "7 records" was on screen for a 30-row dataset: the Admin route counted
 * the KEYS of the Platform's `record_ids` map — one per object type — and
 * printed that as a record count. The route now projects the batch through
 * `sample_data_summary.summarise_sample_batch`, which sums rows and also
 * reports `object_count`; this sentence shows BOTH numbers with their units
 * ("30 records across 7 objects") so that a reader can never again take the
 * number of types for the number of rows, and a failed-row count is named
 * beside them when the batch was partial. Fields an older server does not
 * send simply do not appear — a bare row count is still a true sentence.
 */
export const describeSampleCount = (sample) => {
  const created = Number(sample?.created_count) || 0;
  const failed = Number(sample?.failed_count) || 0;
  const objects = Number.isInteger(sample?.object_count) && sample.object_count > 0
    ? sample.object_count : null;
  if (created <= 0 && failed <= 0) return null;
  let text = plural(created, 'record', 'records');
  if (objects !== null) text += ` across ${plural(objects, 'object', 'objects')}`;
  if (failed > 0) text += `; ${plural(failed, 'row', 'rows')} failed`;
  return text;
};
